// src/pages/CheckOpsPanel.js
// 複製自 MemberOpsPanel，新增：
// 1. SGLRT001 條碼 → 進出場打卡（不走會員搜尋）
// 2. 座位預約按鈕 + Modal 彈窗
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import TodayBookingsList from './TodayBookingsList';
import { formatPhoneInput, PHONE_INPUT_HINT } from '../utils/phone';
import { buildSelectableLockerOptions, lockerOptionLabel } from '../utils/lockerSelectableOptions';
import { parseCouponScan } from '../utils/couponScan';
import { resolveDisplayCode } from '../utils/redeemDisplayCode';
import CouponUsedNoticeModal from '../components/CouponUsedNoticeModal';
axios.defaults.withCredentials = true;

const BASE_LEVELS = ['一般顧客', '年會員', '月會員', '員工'];
const getDisplayLevel = (level, pointsEarned) =>
  (level === '年會員' && (pointsEarned || 0) >= 20) ? '資深年會員' : (level || '一般顧客');

const bizDayYmd = () => {
  const now = new Date();
  if (now.getHours() < 1) {
    now.setDate(now.getDate() - 1);
  }
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
};
const todayYmd = () => bizDayYmd();
const addMonths = (ymd, months) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setMonth(dt.getMonth() + months);
  const mm = String(dt.getMonth() + 1).padStart(2, '0'), dd2 = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd2}`;
};
const addYears = (ymd, years) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y + years, m - 1, d);
  const mm = String(dt.getMonth() + 1).padStart(2, '0'), dd2 = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd2}`;
};
const levelPillColor = (lvl) =>
  (/資深/.test(lvl) ? '#7c3aed'
    : (/年會員|月會員/.test(lvl) ? '#2563eb'
      : (/員工/.test(lvl) ? '#f97316' : '#6b7280')));

function SearchKeypad({ onDigit, onDelete }) {
  const wrap = { display:'grid', gridTemplateColumns:'repeat(12, 1fr)', gap:6, marginTop:8 };
  const btn  = { height:38, borderRadius:10, border:'1px solid #ccc', background:'#fff', fontSize:15, fontWeight:800, cursor:'pointer', padding:0 };
  const keys = ['0','1','2','3','4','5','6','7','8','9','+','刪除'];
  return (
    <div style={wrap}>
      {keys.map(k => (
        <button key={k} style={{ ...btn, ...(k==='刪除' ? { fontSize:13 } : null) }} onClick={() => (k==='刪除' ? onDelete() : onDigit(k))}>
          {k}
        </button>
      ))}
    </div>
  );
}

function SmallNumpad({ onPress }) {
  const keyBtn = { height: 32, border: '1px solid #ddd', borderRadius: 10, background: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', padding: 0 };
  const grid = { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:6 };
  const keys = ['1','2','3','4','5','6','7','8','9','00','0','⌫'];
  return (
    <div style={grid}>
      {keys.map(k => (
        <button key={k} style={{...keyBtn, ...(k==='⌫' ? { fontSize:13 } : null)}} onClick={() => onPress(k)} title={k==='⌫' ? '刪除' : ''}>
          {k}
        </button>
      ))}
    </div>
  );
}

function Modal({ open, onClose, children, width = 460 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div onClick={(e)=>e.stopPropagation()} style={{width:'100%',maxWidth:width,background:'#fff',borderRadius:12,boxShadow:'0 10px 30px rgba(0,0,0,.25)',padding:'14px'}}>
        {children}
      </div>
    </div>
  );
}

function FullScreenModal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:9998,display:'flex',flexDirection:'column',padding:16}}>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
        <button onClick={onClose} style={{padding:'8px 16px',fontSize:14,fontWeight:700,borderRadius:8,border:'none',background:'#fff',cursor:'pointer',boxShadow:'0 2px 8px rgba(0,0,0,.15)'}}>
          ✕ 關閉預約
        </button>
      </div>
      <div style={{flex:1,minHeight:0,background:'#fff',borderRadius:12,overflow:'hidden',boxShadow:'0 10px 40px rgba(0,0,0,.25)'}}>
        {children}
      </div>
    </div>
  );
}

const fmtScanTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  // DB useUTC=true → JSON date 的 UTC 分量 = SQL 本地時間
  return `${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}:${String(d.getUTCSeconds()).padStart(2,'0')}`;
};

export default function CheckOpsPanel({ fullBleed = false, onAttendanceScan }){
  const navigate = useNavigate();
  const location = useLocation();
  const operateLogsPath = location.pathname.startsWith('/count')
    ? '/count/operate-logs'
    : '/admin/operate-logs';
  const inputRef = useRef(null);
  const [scanMode, setScanMode] = useState(true);
  const [search, setSearch] = useState('');
  const [member, setMember] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [searchKbOpen, setSearchKbOpen] = useState(false);

  // 座位預約彈窗
  const [bookingOpen, setBookingOpen] = useState(false);

  // 進出場：進場設定彈窗
  const [checkinDlg, setCheckinDlg] = useState({
    open: false, barcode: '', paidHours: 2, customerType: '一般顧客', isNightOwl: false, busy: false,
  });
  // 進出場掃碼結果彈窗
  const [attendanceResult, setAttendanceResult] = useState({ open: false, data: null });

  // Level dialog
  const [levelDlg, setLevelDlg] = useState({
    open:false, level:'年會員', start:'', expiry:'', name:'', phone:'', origLevel:'', origExpiry:''
  });
  const [lockerDlg, setLockerDlg] = useState({
    open: false,
    locker_number: '',
    expiry_date: '',
    pendingVacatedAck: null,
    vacatedAckChecked: false,
  });
  const [lockerPickList, setLockerPickList] = useState({ loading: false, error: '', slots: [] });

  const [target, setTarget] = useState('point');
  const [amount, setAmount] = useState('1');
  const [isDirty, setIsDirty] = useState(false);
  const [opConfirm, setOpConfirm] = useState({ open:false, type:'pass', action:'use', amount:0, busy:false, doneMsg:'' });

  const [memberCoupons, setMemberCoupons] = useState([]);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponMsg, setCouponMsg] = useState('');
  const [couponConfirm, setCouponConfirm] = useState({ open:false, coupon:null, busy:false, doneMsg:'' });
  const [couponUsedNotice, setCouponUsedNotice] = useState({ open:false, couponName:'', claimId:'', displayCode:'', usedAt:'' });
  const [couponAlertOpen, setCouponAlertOpen] = useState(false);
  const [couponAlertDismissedFor, setCouponAlertDismissedFor] = useState(null);

  const [nightPassword, setNightPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/api/admin/night-password');
        setNightPassword(data?.password || '');
      } catch {
        setNightPassword('');
      }
    })();
  }, []);

  useEffect(() => {
    if (!lockerDlg.open || !member?.ID) return;
    let cancelled = false;
    setLockerPickList((p) => ({ ...p, loading: true, error: '' }));
    (async () => {
      try {
        const { data } = await axios.get('/api/admin/lockers/overview');
        if (cancelled) return;
        setLockerPickList({
          loading: false,
          error: '',
          slots: Array.isArray(data?.slots) ? data.slots : [],
        });
      } catch {
        if (!cancelled) {
          setLockerPickList({ loading: false, error: '無法載入可選櫃位', slots: [] });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [lockerDlg.open, member?.ID]);

  const lockerSelectOptions = useMemo(() => {
    const base = buildSelectableLockerOptions(lockerPickList.slots, member?.ID);
    const cur = String(lockerDlg.locker_number || '').trim();
    if (!cur) return base;
    if (base.some((o) => String(o.number) === cur)) return base;
    return [
      ...base,
      {
        number: cur,
        needsVacatedAck: false,
        duplicateWarn: false,
        variant: 'orphan',
      },
    ].sort((a, b) => Number(a.number) - Number(b.number));
  }, [lockerPickList.slots, member?.ID, lockerDlg.locker_number]);

  const selectedLockerPick = useMemo(() => {
    const v = String(lockerDlg.locker_number || '').trim();
    if (!v) return null;
    return lockerSelectOptions.find((o) => String(o.number) === v) || null;
  }, [lockerSelectOptions, lockerDlg.locker_number]);

  const refreshNightPassword = async () => {
    setPasswordLoading(true);
    try {
      const { data } = await axios.post('/api/admin/night-password');
      const newPwd = data?.password || '';
      setNightPassword(newPwd);
      setMsg(newPwd ? `已取得新密碼：${newPwd}` : '已更新夜間密碼');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      const errMsg = e?.response?.data?.error || '更新失敗';
      const detail = e?.response?.data?.detail ? `（${e?.response?.data?.detail}）` : '';
      setMsg(`${errMsg}${detail}`);
      setTimeout(() => setMsg(''), 3000);
    } finally {
      setPasswordLoading(false);
    }
  };

  // ★ 條碼前綴判斷：SGLRT001 -> 進出場打卡
  const isAttendanceBarcode = (val) => String(val || '').trim().startsWith('SGLRT001');

  // 前端計費預覽（與後端 calcFee 邏輯一致）
  const PRICING = {
    '一般顧客':   { base: 200, extra: 50 },
    '年會員':     { base: 179, extra: 40 },
    '資深年會員': { base: 175, extra: 25 },
    '月會員':     { base: 0,   extra: 0  },
  };
  const isNowAfter19 = () => new Date().getHours() >= 19;
  const getNightOwlFee = (ct) => {
    if (ct === '月會員') return 0;
    if (ct === '年會員' || ct === '資深年會員') return 100;
    return 200; // 一般顧客
  };
  const calcFeePreview = (ct, hours, owl) => {
    const r = PRICING[ct] || PRICING['一般顧客'];
    if (r.base === 0 && r.extra === 0) return 0;
    const owlFee = getNightOwlFee(ct);
    if (isNowAfter19()) return owlFee; // 19 點後進場只收夜貓費
    const cappedHours = Math.min(hours, 7);
    let fee = r.base;
    if (cappedHours > 2) fee += (cappedHours - 2) * r.extra;
    if (owl) fee += owlFee;
    return fee;
  };

  const openCheckinDialog = (barcode) => {
    setCheckinDlg({
      open: true, barcode: barcode.trim(),
      paidHours: 2, customerType: '一般顧客', isNightOwl: false, busy: false,
    });
    if (scanMode) {
      setSearch('');
    }
  };

  const submitCheckin = async () => {
    const { barcode, paidHours, customerType, isNightOwl } = checkinDlg;
    setCheckinDlg((p) => ({ ...p, busy: true }));
    try {
      setErr(''); setMsg('');
      const { data } = await axios.post('/api/admin/attendance/scan', {
        barcode, paidHours, customerType, isNightOwl,
      });
      setCheckinDlg((p) => ({ ...p, open: false, busy: false }));
      setAttendanceResult({ open: true, data });
      onAttendanceScan?.();
      setTimeout(() => inputRef.current?.focus?.(), 0);
    } catch (e) {
      setCheckinDlg((p) => ({ ...p, busy: false }));
      const errMsg = e?.response?.data?.error || '打卡失敗';
      setErr(errMsg);
      setTimeout(() => setErr(''), 2500);
    }
  };

  const handleAttendanceScan = async (barcode) => {
    try {
      setErr(''); setMsg('');
      // 先查詢是否有未出場記錄（直接 POST 不帶進場參數，後端會判斷）
      const { data } = await axios.post('/api/admin/attendance/scan', { barcode: barcode.trim() });
      if (data.action === 'check_out') {
        setAttendanceResult({ open: true, data });
        onAttendanceScan?.();
        if (scanMode) {
          setSearch('');
          setTimeout(() => inputRef.current?.focus?.(), 0);
        }
      } else {
        // check_in 回來表示沒有未出場記錄 -> 不應直接進場，應開設定面板
        // 但因 API 已經建立了記錄，這條路徑不應走到。做法改為先 peek 再決定。
        setAttendanceResult({ open: true, data });
        onAttendanceScan?.();
        if (scanMode) {
          setSearch('');
          setTimeout(() => inputRef.current?.focus?.(), 0);
        }
      }
    } catch (e) {
      const errMsg = e?.response?.data?.error || '打卡失敗';
      setErr(errMsg);
      setTimeout(() => setErr(''), 2500);
    }
  };

  const handleBarcodeScan = async (barcode) => {
    try {
      setErr(''); setMsg('');
      // 先用 GET today 查該條碼是否有未出場記錄
      const { data: todayData } = await axios.get('/api/admin/attendance/today');
      const openRecord = (todayData?.list || []).find(
        (r) => r.BARCODE === barcode.trim() && !r.CHECK_OUT
      );
      if (openRecord) {
        // 有未出場記錄 -> 直接出場
        const { data } = await axios.post('/api/admin/attendance/scan', { barcode: barcode.trim() });
        setAttendanceResult({ open: true, data });
        onAttendanceScan?.();
        if (scanMode) {
          setSearch('');
          setTimeout(() => inputRef.current?.focus?.(), 0);
        }
      } else {
        // 沒有未出場記錄 -> 開啟進場設定彈窗
        openCheckinDialog(barcode);
      }
    } catch (e) {
      const errMsg = e?.response?.data?.error || '查詢失敗';
      setErr(errMsg);
      setTimeout(() => setErr(''), 2500);
    }
  };

  const handleSearchKeyDown = async (e) => {
    if (e.key === 'Escape') { setSearch(''); return; }
    if (e.key === 'Enter') {
      const val = search.trim();
      if (isAttendanceBarcode(val)) {
        await handleBarcodeScan(val);
      } else if (await handleCouponScanInput(val)) {
        if (scanMode) {
          setSearch('');
          setTimeout(() => inputRef.current?.focus?.(), 0);
        }
      } else {
        await searchAndLog();
        if (scanMode) {
          setSearch('');
          setTimeout(() => inputRef.current?.focus?.(), 0);
        }
      }
    }
  };

  const normalizePhoneInput = (text) => formatPhoneInput(text);

  const loadMemberCoupons = async (memberId) => {
    if (!memberId) return [];
    try {
      const { data } = await axios.get(`/api/admin/member/${memberId}/coupons`);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setMemberCoupons(rows);
      return rows;
    } catch {
      setMemberCoupons([]); setCouponMsg('無法載入優惠券');
      setTimeout(() => setCouponMsg(''), 2000);
      return [];
    }
  };

  const loadMemberById = async (memberId) => {
    if (!memberId) return null;
    try {
      const res = await axios.get(`/api/admin/member/${memberId}`);
      setMember(res.data);
      await loadMemberCoupons(memberId);
      return res.data;
    } catch {
      return null;
    }
  };

  const openCouponConfirmFromPreview = (claim) => {
    if (!claim) return;
    setCouponConfirm({
      open: true,
      coupon: {
        CLAIM_ID: claim.claimId,
        DISPLAY_CODE: claim.displayCode,
        NAME: claim.couponName,
        DESCRIPTION: claim.description,
        END_AT: claim.endAt,
        phoneTail: claim.phoneTail,
        emailMask: claim.emailMask,
        memberName: claim.memberName,
      },
      busy: false,
      doneMsg: '',
    });
  };

  const openCouponUsedNotice = (claim) => {
    if (!claim) return;
    setCouponUsedNotice({
      open: true,
      couponName: claim.couponName || claim.NAME || '',
      claimId: claim.claimId || claim.CLAIM_ID || '',
      displayCode: resolveDisplayCode(claim.claimId || claim.CLAIM_ID, claim.displayCode || claim.DISPLAY_CODE),
      usedAt: claim.usedAt ? String(claim.usedAt).replace('T', ' ').slice(0, 19) : '',
    });
  };

  const handleCouponScanInput = async (qRaw) => {
    const parsed = parseCouponScan(qRaw);
    if (!parsed) return false;
    setErr('');
    try {
      let claim;
      if (parsed.type === 'token') {
        const { data } = await axios.get(`/api/admin/coupon/claims/by-token/${encodeURIComponent(parsed.value)}`);
        claim = data?.claim;
      } else {
        const { data } = await axios.get(`/api/admin/coupon/claims/${parsed.value}/preview`);
        claim = data?.claim;
      }
      if (!claim) { setErr('找不到此優惠券'); return true; }
      if (claim.isUsed) {
        openCouponUsedNotice(claim);
        return true;
      }
      if (claim.memberId) {
        await loadMemberById(claim.memberId);
      } else {
        setMember(null);
        setMemberCoupons([]);
      }
      openCouponConfirmFromPreview(claim);
      return true;
    } catch {
      setErr('優惠券查詢失敗');
      return true;
    }
  };

  const doSearch = async (qOverride) => {
    const qRaw = String(qOverride ?? search ?? '').trim();
    if (!qRaw) { setErr('請輸入搜尋內容'); throw new Error('no_query'); }
    try {
      setErr(''); setMsg(''); setCouponMsg('');
      const encoded = encodeURIComponent(qRaw);
      let url = `/api/admin/member/phone/${encoded}`;
      let data;
      try {
        const res = await axios.get(url);
        data = res.data;
      } catch (phoneError) {
        url = `/api/admin/member/${encoded}`;
        const res = await axios.get(url);
        data = res.data;
      }
      setMember(data);
      await loadMemberCoupons(data.ID);
      setAmount('1'); setIsDirty(false);
      return data;
    } catch (e) {
      setMember(null); setMemberCoupons([]); setErr('查無此會員');
      throw e;
    }
  };

  const searchAndLog = async () => {
    const qRaw = String(search || '').trim();
    if (qRaw && (await handleCouponScanInput(qRaw))) {
      if (scanMode) {
        setSearch('');
        setTimeout(() => inputRef.current?.focus?.(), 0);
      }
      return;
    }
    try {
      const found = await doSearch(search || member?.PHONE || member?.ID);
      if (!found?.ID) throw new Error('no_member');
      await axios.post('/api/admin/search-entry', {
        phone: found.PHONE,
        source: 'check-ops-panel'
      });
      if (scanMode) {
        setSearch('');
        setTimeout(() => inputRef.current?.focus?.(), 0);
      }
      setMsg('已查詢並記錄'); setTimeout(() => setMsg(''), 1600);
    } catch (e) {
      setMsg(e?.response?.data?.error || '查詢失敗'); setTimeout(() => setMsg(''), 1800);
    }
  };

  const handleSearchClick = async () => {
    const val = search.trim();
    if (isAttendanceBarcode(val)) {
      await handleBarcodeScan(val);
    } else {
      await searchAndLog();
    }
  };

  const openDailyLog = () => {
    navigate(operateLogsPath);
  };

  const openLevelDialog = () => {
    if (!member) return;
    const start = todayYmd();
    const lvl = member?.LEVEL || '一般顧客';
    let expiry = member?.MEMBER_EXPIRY?.split?.('T')?.[0] || '';
    if (!expiry) {
      if (lvl === '年會員') expiry = addYears(start, 1);
      else if (lvl === '月會員') expiry = addMonths(start, 1);
    }
    setLevelDlg({
      open:true, level:lvl, start, expiry,
      name: member?.NAME || '', phone: member?.PHONE || '',
      origLevel: lvl, origExpiry: expiry || ''
    });
  };
  const handleLevelChange = (lvl) => {
    const base = levelDlg.start || todayYmd();
    let expiry = levelDlg.expiry;
    if (lvl === '年會員') expiry = addYears(base, 1);
    else if (lvl === '月會員') expiry = addMonths(base, 1);
    else expiry = '';
    setLevelDlg((p) => ({ ...p, level:lvl, expiry }));
  };
  const handleStartChange = (s) => {
    let expiry = levelDlg.expiry;
    if (levelDlg.level === '年會員') expiry = addYears(s, 1);
    else if (levelDlg.level === '月會員') expiry = addMonths(s, 1);
    setLevelDlg((p) => ({ ...p, start:s, expiry }));
  };
  const submitLevelChange = async () => {
    if (!member?.ID) return;
    try {
      await axios.post(`/api/admin/member/${member.ID}/level`, {
        level: levelDlg.level,
        start_date: levelDlg.start || todayYmd(),
        expiry_date: levelDlg.expiry || null,
        note: '後台調整'
      });
      const { data: updated } = await axios.get(`/api/admin/member/id/${member.ID}`);
      setMember(updated);
      setLevelDlg({ open:false, level:'年會員', start:'', expiry:'', name:'', phone:'', origLevel:'', origExpiry:'' });
      setMsg('會員身分已更新'); setTimeout(() => setMsg(''), 1600);
    } catch (e) {
      setMsg(e?.response?.data?.error || '更新失敗'); setTimeout(() => setMsg(''), 2000);
    }
  };

  const submitLocker = async () => {
    if (!member?.ID) return;
    const num = String(lockerDlg.locker_number || '').trim();
    if (!num) { setMsg('請選擇櫃號'); setTimeout(() => setMsg(''), 1500); return; }
    const body = {
      locker_number: num,
      expiry_date: lockerDlg.expiry_date || addMonths(todayYmd(), 1),
    };
    if (Array.isArray(lockerDlg.pendingVacatedAck) && lockerDlg.pendingVacatedAck.length > 0) {
      if (!lockerDlg.vacatedAckChecked) {
        window.alert('請勾選「已確認過期客人已實際退租／櫃位已騰空」後再儲存。');
        return;
      }
      body.confirm_expired_previous_vacated = true;
    }
    try {
      const { data } = await axios.post(`/api/admin/member/${member.ID}/locker`, body);
      if (data?.warning) window.alert(data.warning);
      const { data: updated } = await axios.get(`/api/admin/member/id/${member.ID}`);
      setMember(updated);
      setLockerDlg({
        open: false,
        locker_number: '',
        expiry_date: '',
        pendingVacatedAck: null,
        vacatedAckChecked: false,
      });
      setMsg('櫃子已設定／續期'); setTimeout(() => setMsg(''), 1600);
    } catch (e) {
      const d = e?.response?.data || {};
      if (d.error === 'locker_expired_needs_ack') {
        setLockerDlg((p) => ({
          ...p,
          pendingVacatedAck: Array.isArray(d.holders) ? d.holders : [],
          vacatedAckChecked: false,
        }));
        const msg = d.message || '請先確認過期客人已退租';
        setMsg(msg); setTimeout(() => setMsg(''), 4000);
        window.alert(msg);
        return;
      }
      const msg = d.message || d.error || '設定失敗';
      setMsg(msg); setTimeout(() => setMsg(''), 3200);
      window.alert(msg);
    }
  };

  const handlePadPress = (key) => {
    if (key === '⌫') { setIsDirty(true); setAmount((p) => String(p || '').slice(0, -1)); return; }
    const digits = String(key).replace(/[^\d]/g, ''); if (!digits) return;
    if (!isDirty) { setAmount(digits); setIsDirty(true); } else { setAmount((p) => String(p || '') + digits); }
  };
  const postOperate = async (val, opType) => {
    const { data } = await axios.post(`/api/admin/member/${member.ID}/operate`, { type: opType || target, value: val });
    const { data: updated } = await axios.get(`/api/admin/member/id/${member.ID}`);
    setMember(updated); setAmount('1'); setIsDirty(false);
    return data?.msg || '操作完成';
  };
  const openOperationConfirm = (isAdd, opType) => {
    if (!member) return;
    const t = opType || target;
    const amt = Math.abs(Number(amount) || 0);
    if (!amt) { setMsg('請輸入數字'); setTimeout(()=>setMsg(''),1500); return; }
    setOpConfirm({ open:true, type:t, action:isAdd?'add':'use', amount:amt, busy:false, doneMsg:'' });
  };
  const confirmOperation = async () => {
    const { type, action, amount: amt } = opConfirm;
    try {
      setOpConfirm((p) => ({ ...p, busy:true }));
      const val = action === 'add' ? +amt : -Math.abs(+amt);
      const text = await postOperate(val, type);
      const done = type === 'pass' ? (action === 'add' ? `已發放 ${amt} 張日券` : `已扣除 ${amt} 張日券`)
                                   : (action === 'add' ? `已增加 ${amt} 點`    : `已扣除 ${amt} 點`);
      setOpConfirm((p) => ({ ...p, busy:false, doneMsg:done })); setMsg(text || done);
    } catch (e) {
      setOpConfirm((p) => ({ ...p, busy:false })); setMsg(e?.response?.data?.error || '操作失敗'); setTimeout(()=>setMsg(''),1800);
    }
  };

  const redeemClaimCore = async (claimId) => {
    setCouponBusy(true);
    try {
      await axios.post(`/api/admin/coupon/claims/${Number(claimId)}/use`);
      await loadMemberCoupons(member?.ID);
    } finally { setCouponBusy(false); }
  };
  const openCouponConfirm = (couponObj) => {
    if (!couponObj) return;
    setCouponConfirm({ open:true, coupon:couponObj, busy:false, doneMsg:'' });
  };
  const confirmRedeemCoupon = async () => {
    const c = couponConfirm.coupon; if (!c) return;
    try {
      setCouponConfirm((p) => ({ ...p, busy:true }));
      await redeemClaimCore(c.CLAIM_ID);
      setCouponConfirm((p) => ({ ...p, busy:false, doneMsg:`已核銷：${c.NAME}` }));
      setCouponMsg('核銷完成'); setTimeout(()=>setCouponMsg(''),1800);
    } catch (e) {
      setCouponConfirm((p) => ({ ...p, busy:false }));
      const status = e?.response?.status; const detail = e?.response?.data?.error || e?.message || '';
      if (status === 409 || detail === 'already_used' || detail === 'ALREADY_USED') {
        setCouponConfirm({ open:false, coupon:null, busy:false, doneMsg:'' });
        openCouponUsedNotice({
          couponName: c.NAME,
          claimId: c.CLAIM_ID,
          displayCode: resolveDisplayCode(c.CLAIM_ID, c.DISPLAY_CODE),
        });
        return;
      }
      setCouponMsg(`核銷失敗（${status || 'ERR'}：${detail || ''}）`); setTimeout(()=>setCouponMsg(''),2200);
    }
  };

  useEffect(() => {
    if (!member?.ID) return;
    const unused = memberCoupons.filter((c) => !c.USED_AT && !c.IS_USED);
    if (unused.length === 0) return;
    if (couponAlertDismissedFor === member.ID) return;
    setCouponAlertOpen(true);
  }, [member?.ID, memberCoupons, couponAlertDismissedFor]);

  const expired = useMemo(() => {
    const exp = member?.MEMBER_EXPIRY?.split?.('T')?.[0]; if (!exp) return false;
    return new Date(exp) < new Date(todayYmd());
  }, [member]);

  const [expiredAlertShown, setExpiredAlertShown] = useState(false);
  const [dismissedMemberId, setDismissedMemberId] = useState(null);
  
  useEffect(() => {
    if (member?.ID !== dismissedMemberId) {
      setDismissedMemberId(null);
    }
    if (member && expired && member?.ID !== dismissedMemberId) {
      setExpiredAlertShown(true);
    } else if (!member || !expired) {
      setExpiredAlertShown(false);
    }
  }, [member, expired, dismissedMemberId]);

  const pill = (txt, bg, color='#111') =>
    <span style={{padding:'2px 8px',borderRadius:999,fontSize:12,fontWeight:900,background:bg,color}}>{txt}</span>;

  const containerStyle = fullBleed
    ? { background:'linear-gradient(135deg, #ffffff 0%, #fef3f2 100%)', height:'100%', display:'grid', gridTemplateRows:'1fr', border:'none', borderRadius:0, overflow:'hidden' }
    : { background:'linear-gradient(135deg, #ffffff 0%, #fef3f2 100%)', height:'100%', display:'grid', gridTemplateRows:'1fr', border:'2px solid #fed7d7', borderRadius:12, overflow:'hidden', boxShadow: '0 2px 8px rgba(245, 87, 108, 0.1)' };

  return (
    <section className="card" style={containerStyle}>
      <style>{`
        .muted{ opacity:.7 }
        .btn{ padding:10px 14px; border-radius:10px; }
        .btn-dark{ background:#111; color:#fff; }
        .btn-outline{ background:#fafafa; border:1px solid #ddd; }
      `}</style>

      <div className="card-body" style={{minHeight:0, overflow:'auto', padding:'8px 10px', display:'grid', gap:8}}>
        <div style={{border:'1px solid #e7e7e7',borderRadius:8,background:'#fff',padding:8}}>
          <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
            <input
              ref={inputRef}
              type="tel"
              inputMode="numeric"
              style={{flex:1,minWidth:200,padding:'11px 14px',fontSize:17,border:'2px solid #fed7d7',borderRadius:12,transition:'border-color 0.2s'}}
              onFocus={(e) => e.target.style.borderColor = '#f5576c'}
              onBlur={(e) => e.target.style.borderColor = '#fed7d7'}
              placeholder="輸入電話、ID 或掃碼（SGLRT001=打卡）"
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
            />
            <button onClick={handleSearchClick} className="btn btn-dark" style={{fontSize:14,fontWeight:800,background:'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',boxShadow:'0 4px 12px rgba(245, 87, 108, 0.3)'}}>🔍 搜尋（記錄）</button>
            <button onClick={()=>setSearch('')} className="btn btn-outline" style={{fontSize:13,background:'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',border:'2px solid #e0e7ff'}}>清空</button>
            <button className="btn btn-outline" onClick={()=>setSearchKbOpen(true)} style={{fontSize:13,background:'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',border:'2px solid #e0e7ff'}}>⌨️ 鍵盤</button>
            <button className="btn btn-outline" onClick={openDailyLog} style={{fontSize:13,background:'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',border:'2px solid #e0e7ff'}}>📋 操作明細</button>
            <button className="btn" onClick={()=>setBookingOpen(true)} style={{fontSize:13,fontWeight:800,background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',color:'#fff',boxShadow:'0 4px 12px rgba(102, 126, 234, 0.3)'}}>📅 座位預約</button>
            <div style={{display:'flex',gap:6,alignItems:'center',fontSize:13}}>
              <span style={{color:'#666'}}>夜間密碼：</span>
              <span style={{fontWeight:700,fontSize:14}}>{nightPassword || '尚未取得'}</span>
              <button
                onClick={refreshNightPassword}
                disabled={passwordLoading}
                className="btn btn-dark"
                style={{fontSize:12,padding:'6px 12px'}}
              >
                {passwordLoading ? '更新中…' : '變更密碼'}
              </button>
            </div>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,userSelect:'none',marginLeft:'auto'}}>
              <input type="checkbox" checked={scanMode} onChange={(e)=> setScanMode(e.target.checked)} />
              掃碼模式
            </label>
          </div>
          {err && <div style={{color:'#b00020',marginTop:6,fontWeight:700,fontSize:13}}>{err}</div>}
        </div>

        {member && (
          <div style={{
            border:`2px solid ${expired ? '#fde047' : '#fed7d7'}`, 
            borderRadius:14, 
            background: expired 
              ? 'linear-gradient(135deg, #fef9c3 0%, #fde68a 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #fff5f7 100%)', 
            padding:'14px 16px', 
            display:'grid', 
            gap:10,
            boxShadow: expired 
              ? '0 4px 16px rgba(253, 224, 71, 0.3)'
              : '0 2px 8px rgba(245, 87, 108, 0.1)'
          }}>
            <div style={{display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', lineHeight:1.2}}>
              <div style={{fontSize:24, fontWeight:900,color: expired ? '#92400e' : '#1a1a1a'}}>{member.NAME || '—'}</div>
              <div style={{fontSize:16,color: expired ? '#a16207' : '#4a5568'}}>{member.PHONE || '—'}</div>
              {pill(
                member.DISPLAY_LEVEL || getDisplayLevel(member.LEVEL, member.POINTS_EARNED || 0), 
                levelPillColor(member.DISPLAY_LEVEL || getDisplayLevel(member.LEVEL, member.POINTS_EARNED || 0)), 
                '#fff'
              )}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
              <span className="muted" style={{fontSize:14}}>點數</span>
              <b style={{fontSize:18}}>{member.POINTS}</b>
              <span className="muted" style={{fontSize:12}}>累計 {member.POINTS_EARNED}</span>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:18,flexWrap:'wrap'}}>
              <span>日券 <b style={{fontSize:18,marginLeft:6}}>{member.DAY_PASS}</b></span>
              <span className="muted" style={{fontSize:14}}>到期日</span>
              <b style={{fontSize:16}}>{member.MEMBER_EXPIRY?.split?.('T')?.[0] || '—'}</b>
              {expired && pill('已過期', '#fde047')}
            </div>
            {(member.lockerNumber != null && member.lockerNumber !== '') && (
              <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',marginTop:4}}>
                <span className="muted" style={{fontSize:14}}>個人櫃</span>
                <b style={{fontSize:16, color: member.lockerExpired ? '#b91c1c' : undefined}}>
                  {member.lockerNumber}號櫃 {member.lockerExpiry ? `到期 ${String(member.lockerExpiry).split('T')[0]}` : ''}
                  {member.lockerExpired && ' (已到期)'}
                </b>
                <button
                  type="button"
                  onClick={() => setLockerDlg({
                    open: true,
                    locker_number: member.lockerNumber || '',
                    expiry_date: member.lockerExpiry ? String(member.lockerExpiry).split('T')[0] : addMonths(todayYmd(), 1),
                    pendingVacatedAck: null,
                    vacatedAckChecked: false,
                  })}
                  className="btn btn-outline"
                  style={{fontSize:13,padding:'6px 12px'}}
                >
                  設定／續期櫃子
                </button>
              </div>
            )}
            {(member.lockerNumber == null || member.lockerNumber === '') && (
              <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',marginTop:4}}>
                <span className="muted" style={{fontSize:14}}>個人櫃</span>
                <span style={{fontSize:14,opacity:.7}}>無</span>
                <button
                  type="button"
                  onClick={() => setLockerDlg({
                    open: true,
                    locker_number: '',
                    expiry_date: addMonths(todayYmd(), 1),
                    pendingVacatedAck: null,
                    vacatedAckChecked: false,
                  })}
                  className="btn btn-outline"
                  style={{fontSize:13,padding:'6px 12px'}}
                >
                  設定櫃子
                </button>
              </div>
            )}
          </div>
        )}

        {member && (
          <div style={{border:'2px solid #fed7d7',borderRadius:14,background:'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',padding:14,display:'grid',gap:12,boxShadow: '0 2px 6px rgba(245, 87, 108, 0.08)'}}>
            <div style={{
              display:'grid',
              gridTemplateColumns:'minmax(170px, 210px) 1fr',
              gap:10,
              alignItems:'start',
              minWidth:0,
            }}>
              <div style={{display:'grid', gap:8, minWidth:0}}>
                <input
                  value={amount}
                  placeholder="輸入數字"
                  onChange={(e)=>{ const v = e.target.value.replace(/[^\d]/g,''); setAmount(v); if(!isDirty) setIsDirty(true); }}
                  style={{width:'100%',padding:8,fontSize:15,textAlign:'right',border:'1px solid #ccc',borderRadius:8}}
                />
                <SmallNumpad onPress={handlePadPress} />
              </div>
              <div style={{display:'grid', gap:8, justifyItems:'start'}}>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, width:'100%', maxWidth:340}}>
                  <button onClick={() => openOperationConfirm(true, 'point')}  className="btn" style={{background:'linear-gradient(135deg,#10b981,#059669)',color:'#fff',fontSize:14,fontWeight:900,padding:'10px 8px',borderRadius:10,border:'none',cursor:'pointer'}}>＋點數 增加</button>
                  <button onClick={() => openOperationConfirm(true, 'pass')}   className="btn" style={{background:'linear-gradient(135deg,#3b82f6,#2563eb)',color:'#fff',fontSize:14,fontWeight:900,padding:'10px 8px',borderRadius:10,border:'none',cursor:'pointer'}}>＋日券 發放</button>
                  <button onClick={() => openOperationConfirm(false, 'point')} className="btn" style={{background:'linear-gradient(135deg,#ef4444,#dc2626)',color:'#fff',fontSize:14,fontWeight:900,padding:'10px 8px',borderRadius:10,border:'none',cursor:'pointer'}}>−點數 扣除</button>
                  <button onClick={() => openOperationConfirm(false, 'pass')}  className="btn" style={{background:'linear-gradient(135deg,#f59e0b,#d97706)',color:'#fff',fontSize:14,fontWeight:900,padding:'10px 8px',borderRadius:10,border:'none',cursor:'pointer'}}>−日券 扣除</button>
                </div>
                <button onClick={openLevelDialog} className="btn btn-outline" style={{fontSize:14,fontWeight:900,width:160}}>調整會員身分</button>
              </div>
            </div>
            {msg && <div style={{color:'#0a7',fontWeight:700,marginTop:2,fontSize:13}}> {msg} </div>}
          </div>
        )}

        {member && (
          <div style={{border:'2px solid #fed7d7',borderRadius:14,background:'linear-gradient(135deg, #ffffff 0%, #fff5f7 100%)',padding:12,boxShadow: '0 2px 6px rgba(245, 87, 108, 0.08)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
              <div style={{fontWeight:900, fontSize:15}}>優惠券</div>
              {couponMsg && <div style={{color:'#0a7',fontWeight:700,fontSize:13}}>{couponMsg}</div>}
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:14,tableLayout:'fixed'}}>
              <colgroup><col /><col style={{width:'90px'}}/></colgroup>
              <thead>
                <tr>
                  <th align="left" style={{padding:'8px 10px'}}>內容</th>
                  <th align="left" style={{padding:'8px 10px'}}>操作</th>
                </tr>
              </thead>
              <tbody>
                {memberCoupons.filter(c => !c.USED_AT).map(c => (
                  <tr key={String(c.CLAIM_ID)}>
                    <td style={{padding:'10px'}}>
                      <div style={{fontWeight:800, fontSize:15}} title={c.NAME}>{c.NAME}</div>
                      {c.DESCRIPTION && (
                        <div style={{fontSize:13, color:'#555', marginTop:4, whiteSpace:'pre-wrap'}}>
                          {c.DESCRIPTION}
                        </div>
                      )}
                      <div style={{fontSize:12, opacity:.7, marginTop:6}}>
                        到期：{c.END_AT || '—'}
                      </div>
                    </td>
                    <td style={{padding:'8px 10px'}}>
                      <button disabled={couponBusy} onClick={()=>openCouponConfirm(c)} style={{padding:'6px 10px',fontSize:13}}>
                        {couponBusy ? '處理中…' : '使用'}
                      </button>
                    </td>
                  </tr>
                ))}
                {memberCoupons.filter(c => !c.USED_AT).length === 0 && (
                  <tr><td colSpan={2} style={{padding:'10px',opacity:.6}}>沒有未使用的優惠券</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 進場設定彈窗 */}
      <Modal open={checkinDlg.open} onClose={() => !checkinDlg.busy && setCheckinDlg((p) => ({ ...p, open: false }))} width={520}>
        <h3 style={{ margin: '0 0 14px', fontSize: 20, fontWeight: 900 }}>進場設定</h3>
        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'monospace', marginBottom: 12, color: '#333' }}>
          條碼：{checkinDlg.barcode}
        </div>

        {/* 已付時數 */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>已付時數</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[2, 3, 4, 5, 6, 7].map((h) => (
              <button
                key={h}
                onClick={() => setCheckinDlg((p) => ({ ...p, paidHours: h }))}
                style={{
                  flex: 1, padding: '10px 0', fontSize: 18, fontWeight: 900, borderRadius: 10, cursor: 'pointer',
                  border: checkinDlg.paidHours === h ? '2px solid #2563eb' : '2px solid #ddd',
                  background: checkinDlg.paidHours === h ? 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)' : '#fff',
                  color: checkinDlg.paidHours === h ? '#1e40af' : '#333',
                }}
              >
                {h}h
              </button>
            ))}
          </div>
        </div>

        {/* 進場身分 */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>進場身分</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {['一般顧客', '年會員', '資深年會員', '月會員'].map((ct) => (
              <label key={ct} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700,
                border: checkinDlg.customerType === ct ? '2px solid #2563eb' : '2px solid #ddd',
                background: checkinDlg.customerType === ct ? '#eff6ff' : '#fff',
              }}>
                <input
                  type="radio" checked={checkinDlg.customerType === ct}
                  onChange={() => setCheckinDlg((p) => ({ ...p, customerType: ct }))}
                />
                {ct}
              </label>
            ))}
          </div>
        </div>

        {/* 夜貓 */}
        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
            borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700,
            border: checkinDlg.isNightOwl ? '2px solid #7c3aed' : '2px solid #ddd',
            background: checkinDlg.isNightOwl ? '#f5f3ff' : '#fff',
          }}>
            <input
              type="checkbox" checked={checkinDlg.isNightOwl}
              onChange={(e) => setCheckinDlg((p) => ({ ...p, isNightOwl: e.target.checked }))}
              style={{ width: 20, height: 20, accentColor: '#7c3aed' }}
            />
            夜貓加購 (+{getNightOwlFee(checkinDlg.customerType)})
          </label>
        </div>

        {/* 預估金額 */}
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          border: '2px solid #86efac',
        }}>
          <span style={{ fontSize: 15, color: '#166534' }}>預估金額：</span>
          <span style={{ fontSize: 24, fontWeight: 900, color: '#166534' }}>
            ${calcFeePreview(checkinDlg.customerType, checkinDlg.paidHours, checkinDlg.isNightOwl)}
          </span>
          {new Date().getHours() >= 19 && (
            <span style={{ fontSize: 13, color: '#92400e', marginLeft: 8 }}>（19點後進場，只收夜貓費）</span>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={() => setCheckinDlg((p) => ({ ...p, open: false }))}
            disabled={checkinDlg.busy}
            style={{ padding: '10px 18px', fontSize: 15, borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
          >
            取消
          </button>
          <button
            onClick={submitCheckin}
            disabled={checkinDlg.busy}
            style={{
              padding: '10px 24px', fontSize: 16, fontWeight: 900, borderRadius: 10, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            }}
          >
            {checkinDlg.busy ? '處理中…' : '確認進場'}
          </button>
        </div>
      </Modal>

      {/* 進出場結果彈窗 */}
      <Modal open={attendanceResult.open} onClose={() => setAttendanceResult({ open: false, data: null })} width={540}>
        {attendanceResult.data && (() => {
          const d = attendanceResult.data;
          const isIn = d.action === 'check_in';
          return (
            <>
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>{isIn ? '🟢' : '🔵'}</div>
                <h3 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 900, color: isIn ? '#059669' : '#2563eb' }}>
                  {isIn ? '進場成功' : '出場成功'}
                </h3>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', marginBottom: 8 }}>{d.BARCODE}</div>
                {d.LABEL && <div style={{ fontSize: 15, color: '#666', marginBottom: 8 }}>{d.LABEL}</div>}

                {/* 進場資訊 */}
                <div style={{ display: 'grid', gap: 6, textAlign: 'left', padding: '0 8px', fontSize: 15, marginBottom: 8 }}>
                  <div>進場時間：<b>{fmtScanTime(d.CHECK_IN)}</b></div>
                  {d.CUSTOMER_TYPE && <div>身分：<b>{d.CUSTOMER_TYPE}</b></div>}
                  {d.PAID_HOURS && <div>已付時數：<b>{d.PAID_HOURS} 小時</b></div>}
                  {d.IS_NIGHT_OWL ? <div>夜貓：<b style={{ color: '#7c3aed' }}>已加購</b></div> : null}
                  {d.AMOUNT_PAID != null && (
                    <div>已付金額：<b style={{ color: '#059669' }}>${d.AMOUNT_PAID}</b></div>
                  )}
                </div>

                {/* 出場詳細計費 */}
                {!isIn && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'grid', gap: 6, textAlign: 'left', padding: '0 8px', fontSize: 15 }}>
                      <div>出場時間：<b>{fmtScanTime(d.CHECK_OUT)}</b></div>
                      <div>
                        總停留：<b>{d.DURATION_MIN != null ? `${Math.floor(d.DURATION_MIN / 60)} 小時 ${d.DURATION_MIN % 60} 分鐘` : '—'}</b>
                        {d.billableHours != null && <span style={{ color: '#666', marginLeft: 6 }}>（計費 {d.billableHours} 小時，超過30分鐘才算下一小時）</span>}
                      </div>
                      {d.extraHours > 0 && (
                        <div style={{ color: '#dc2626', fontWeight: 700 }}>
                          超出時數：{d.extraHours} 小時（已付 {d.paidHours}h，計費 {d.billableHours}h）
                        </div>
                      )}
                    </div>

                    {/* 計費結果 */}
                    <div style={{
                      marginTop: 12, padding: '14px 16px', borderRadius: 12,
                      background: d.AMOUNT_DUE > 0
                        ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                        : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                      border: d.AMOUNT_DUE > 0 ? '2px solid #fca5a5' : '2px solid #86efac',
                    }}>
                      <div style={{ display: 'grid', gap: 6, textAlign: 'left', fontSize: 15 }}>
                        <div>已付金額：<b>${d.AMOUNT_PAID || 0}</b></div>
                        {d.AMOUNT_DUE > 0 && (
                          <div style={{ fontSize: 20, fontWeight: 900, color: '#dc2626' }}>
                            需補繳：${d.AMOUNT_DUE}
                          </div>
                        )}
                        <div style={{ fontSize: 20, fontWeight: 900, color: d.AMOUNT_DUE > 0 ? '#1e40af' : '#059669' }}>
                          總金額：${d.TOTAL_AMOUNT || 0}
                        </div>
                      </div>
                    </div>

                    {/* 夜貓提示 */}
                    {d.needNightOwlPrompt && (
                      <div style={{
                        marginTop: 10, padding: '10px 14px', borderRadius: 10,
                        background: '#fefce8', border: '2px solid #fde047', textAlign: 'left',
                      }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#92400e' }}>
                          ⚠️ 目前已超過 19:00，需加收夜貓費 ${d.nightOwlFee} 元
                        </div>
                        <div style={{ fontSize: 13, color: '#a16207', marginTop: 4 }}>
                          此筆進場時未加購夜貓，{d.CUSTOMER_TYPE === '一般顧客' ? '一般客人 $200' : '會員 $100'}，請另行收費。
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <button
                  onClick={() => setAttendanceResult({ open: false, data: null })}
                  style={{
                    padding: '12px 24px', fontSize: 16, fontWeight: 700,
                    background: isIn
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,.15)',
                  }}
                >
                  確認
                </button>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* 座位預約全螢幕彈窗 */}
      <FullScreenModal open={bookingOpen} onClose={() => setBookingOpen(false)}>
        <div style={{ height: '100%', overflow: 'auto', padding: 12 }}>
          <TodayBookingsList />
        </div>
      </FullScreenModal>

      {/* 點數/日券操作確認 */}
      <Modal open={opConfirm.open} onClose={()=>setOpConfirm({ open:false, type:'pass', action:'use', amount:0, busy:false, doneMsg:'' })} width={620}>
        {!opConfirm.doneMsg ? (
          <>
            <h3 style={{margin:'6px 0 10px',fontSize:19,fontWeight:900}}>
              確認{opConfirm.action==='add' ? (opConfirm.type==='pass'?'發放日券':'增加點數') : (opConfirm.type==='pass'?'扣除日券':'扣除點數')}
            </h3>
            <div style={{marginBottom:14,fontSize:15}}>
              確定要<b style={{fontSize:18,margin:'0 6px'}}>
                {opConfirm.action==='add' ? (opConfirm.type==='pass'?'發放':'增加') : '扣除'}
                {opConfirm.type==='pass' ? ` ${opConfirm.amount} 張日券` : ` ${opConfirm.amount} 點`}
              </b>嗎？
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={()=>setOpConfirm({ open:false, type:'pass', action:'use', amount:0, busy:false, doneMsg:'' })} disabled={opConfirm.busy}>取消</button>
              <button onClick={confirmOperation} disabled={opConfirm.busy} style={{padding:'8px 14px',background:'#111',color:'#fff',borderRadius:10}}>
                {opConfirm.busy ? '處理中…' : '確認'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{margin:'6px 0 10px',fontSize:18,fontWeight:900}}>已完成</h3>
            <div style={{color:'#0a7',fontWeight:800,marginBottom:10,fontSize:15}}>{opConfirm.doneMsg}</div>
            <div style={{display:'flex',justifyContent:'flex-end'}}>
              <button onClick={()=>setOpConfirm({ open:false, type:'pass', action:'use', amount:0, busy:false, doneMsg:'' })}>關閉</button>
            </div>
          </>
        )}
      </Modal>

      {/* 調整會員身分 */}
      <Modal open={levelDlg.open} onClose={()=>setLevelDlg({ open:false, level:'年會員', start:'', expiry:'', name:'', phone:'', origLevel:'', origExpiry:'' })} width={720}>
        <h3 style={{margin:'6px 0 12px',fontSize:20,fontWeight:900}}>調整會員身分</h3>
        <div style={{display:'grid',gridTemplateColumns:'120px 1fr 120px 1fr',gap:10,alignItems:'center',marginBottom:10}}>
          <div style={{textAlign:'right',opacity:.8}}>姓名</div>
          <div style={{fontWeight:700}}>{levelDlg.name || '—'}</div>
          <div style={{textAlign:'right',opacity:.8}}>電話</div>
          <div style={{fontWeight:700}}>{levelDlg.phone || '—'}</div>
        </div>
        <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'center',fontSize:15,marginBottom:10}}>
          <b>等級</b>
          {BASE_LEVELS.map(lvl=>(
            <label key={lvl} style={{display:'flex',alignItems:'center',gap:6}}>
              <input type="radio" checked={levelDlg.level===lvl} onChange={()=>handleLevelChange(lvl)} />
              {lvl}
            </label>
          ))}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'120px 1fr 120px 1fr',gap:10,alignItems:'center'}}>
          <div style={{textAlign:'right',opacity:.8}}>起始日</div>
          <input type="date" value={levelDlg.start} onChange={e=>handleStartChange(e.target.value)} style={{padding:'10px 12px',border:'1px solid #ccc',borderRadius:8,fontSize:15}} />
          <div style={{textAlign:'right',opacity:.8}}>到期日</div>
          <input type="date" value={levelDlg.expiry || ''} onChange={e=>setLevelDlg(p=>({ ...p, expiry:e.target.value }))} style={{padding:'10px 12px',border:'1px solid #ccc',borderRadius:8,fontSize:15}} />
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>
          <button onClick={()=>setLevelDlg({ open:false, level:'年會員', start:'', expiry:'', name:'', phone:'', origLevel:'', origExpiry:'' })}>取消</button>
          <button onClick={submitLevelChange} className="btn btn-dark" style={{fontWeight:900}}>儲存</button>
        </div>
      </Modal>

      {/* 設定／續期櫃子 */}
      <Modal open={lockerDlg.open} onClose={()=>{
        setLockerPickList({ loading: false, error: '', slots: [] });
        setLockerDlg({
          open:false,
          locker_number:'',
          expiry_date:'',
          pendingVacatedAck:null,
          vacatedAckChecked:false,
        });
      }} width={520}>
        <h3 style={{margin:'6px 0 12px',fontSize:18,fontWeight:900}}>設定／續期櫃子</h3>
        <div style={{display:'grid',gridTemplateColumns:'100px 1fr',gap:10,alignItems:'center',marginBottom:10}}>
          <div style={{textAlign:'right',opacity:.8,fontSize:14}}>櫃號</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <select
              value={lockerDlg.locker_number}
              onChange={(e) =>
                setLockerDlg((p) => ({
                  ...p,
                  locker_number: e.target.value,
                  pendingVacatedAck: null,
                  vacatedAckChecked: false,
                }))
              }
              disabled={lockerPickList.loading}
              style={{
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: 8,
                fontSize: 15,
                width: '100%',
                maxWidth: '100%',
              }}
            >
              <option value="">請選擇櫃號</option>
              {lockerSelectOptions.map((opt) => (
                <option
                  key={String(opt.number)}
                  value={String(opt.number)}
                  style={opt.needsVacatedAck ? { color: '#b91c1c', fontWeight: 700 } : undefined}
                >
                  {lockerOptionLabel(opt)}
                </option>
              ))}
            </select>
            {lockerPickList.loading ? (
              <div style={{ fontSize: 12, opacity: 0.75 }}>載入可選櫃位…</div>
            ) : null}
            {lockerPickList.error ? (
              <div style={{ fontSize: 12, color: '#b91c1c' }}>{lockerPickList.error}</div>
            ) : null}
            {selectedLockerPick?.needsVacatedAck ? (
              <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
                已到期未確認退租：請於下方勾選確認後再儲存（後端會再次檢核）。
              </div>
            ) : null}
            {selectedLockerPick?.variant === 'orphan' ? (
              <div style={{ fontSize: 12, color: '#92400e' }}>
                此櫃未出現在可選列表，請確認置物櫃總覽或稍後再試。
              </div>
            ) : null}
          </div>
          <div style={{textAlign:'right',opacity:.8,fontSize:14}}>到期日</div>
          <input
            type="date"
            value={lockerDlg.expiry_date || ''}
            onChange={e=>setLockerDlg(p=>({ ...p, expiry_date:e.target.value }))}
            style={{padding:'10px 12px',border:'1px solid #ccc',borderRadius:8,fontSize:15}}
          />
        </div>
        {Array.isArray(lockerDlg.pendingVacatedAck) && lockerDlg.pendingVacatedAck.length > 0 ? (
          <div style={{ marginBottom: 14, padding: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 13 }}>
            <div style={{ fontWeight: 800, marginBottom: 8, color: '#92400e' }}>過期紀錄須確認退租</div>
            <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
              {lockerDlg.pendingVacatedAck.map((h) => (
                <li key={h.memberId}>
                  {h.name || `會員#${h.memberId}`}
                  {h.expiry ? `（到期 ${String(h.expiry).split('T')[0]}）` : ''}
                </li>
              ))}
            </ul>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={!!lockerDlg.vacatedAckChecked}
                onChange={(e) => setLockerDlg((p) => ({ ...p, vacatedAckChecked: e.target.checked }))}
              />
              <span>我已向客人確認「已實際退租」，置物櫃已騰空，可以登記新租客。</span>
            </label>
          </div>
        ) : null}
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>
          <button onClick={()=>{
            setLockerPickList({ loading: false, error: '', slots: [] });
            setLockerDlg({
              open:false,
              locker_number:'',
              expiry_date:'',
              pendingVacatedAck:null,
              vacatedAckChecked:false,
            });
          }}>取消</button>
          <button onClick={submitLocker} className="btn btn-dark" style={{fontWeight:900}}>儲存</button>
        </div>
      </Modal>

      {/* 搜尋用數字鍵盤 */}
      <Modal open={searchKbOpen} onClose={()=>setSearchKbOpen(false)} width={660}>
        <h3 style={{margin:'6px 0 10px',fontSize:18,fontWeight:900}}>數字鍵盤</h3>
        <div style={{marginBottom:8,opacity:.8,fontSize:14}}>目標欄位：搜尋電話／條碼</div>
        <SearchKeypad
          onDigit={(d)=>{ setSearch(prev => (prev + d)); }}
          onDelete={()=>{ setSearch((prev) => { const cut = String(prev||'').slice(0,-1); return cut === '+' ? '' : cut; }); }}
        />
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:10}}>
          <button onClick={()=>setSearchKbOpen(false)}>完成</button>
        </div>
      </Modal>

      {/* 過期會員提醒彈窗 */}
      <Modal 
        open={expiredAlertShown && member && expired && member?.ID !== dismissedMemberId} 
        onClose={() => {
          setExpiredAlertShown(false);
          if (member?.ID) setDismissedMemberId(member.ID);
        }} 
        width={480}
      >
        <div style={{textAlign: 'center', padding: '8px 0'}}>
          <div style={{fontSize: 48, marginBottom: 12}}>⚠️</div>
          <h3 style={{margin: '0 0 12px 0', fontSize: 22, fontWeight: 900, color: '#92400e'}}>
            會員已過期
          </h3>
          <div style={{marginBottom: 16, fontSize: 18, color: '#78350f', fontWeight: 700}}>
            {member?.NAME || '—'}
          </div>
          <div style={{marginBottom: 16, fontSize: 16, color: '#a16207'}}>
            到期日：<b>{member?.MEMBER_EXPIRY?.split?.('T')?.[0] || '—'}</b>
          </div>
          <div style={{
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #fef9c3 0%, #fde68a 100%)',
            borderRadius: 10, marginBottom: 20, fontSize: 15, color: '#78350f', fontWeight: 600
          }}>
            請提醒會員更新會員資格
          </div>
          <button 
            onClick={() => {
              setExpiredAlertShown(false);
              if (member?.ID) setDismissedMemberId(member.ID);
            }}
            style={{
              padding: '12px 24px', fontSize: 16, fontWeight: 700,
              background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
              color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)'
            }}
          >
            我知道了
          </button>
        </div>
      </Modal>

      {/* 會員有未使用券提醒 */}
      <Modal open={couponAlertOpen} onClose={() => {
        setCouponAlertOpen(false);
        if (member?.ID) setCouponAlertDismissedFor(member.ID);
      }} width={480}>
        <h3 style={{ margin: '6px 0 12px', fontSize: 20, fontWeight: 900 }}>未使用優惠券</h3>
        <p style={{ fontSize: 15, marginBottom: 12 }}>
          此會員有 {memberCoupons.filter((c) => !c.USED_AT && !c.IS_USED).length} 張未使用優惠券
        </p>
        <ul style={{ margin: '0 0 16px', paddingLeft: 20, fontSize: 14 }}>
          {memberCoupons.filter((c) => !c.USED_AT && !c.IS_USED).slice(0, 5).map((c) => (
            <li key={String(c.CLAIM_ID)} style={{ marginBottom: 6 }}>
              {c.NAME}（兌換編號 {resolveDisplayCode(c.CLAIM_ID, c.DISPLAY_CODE)}）
              <button type="button" style={{ marginLeft: 8, fontSize: 12 }} onClick={() => {
                setCouponAlertOpen(false);
                openCouponConfirm(c);
              }}>核銷</button>
            </li>
          ))}
        </ul>
        <button type="button" className="btn btn-dark" onClick={() => {
          setCouponAlertOpen(false);
          if (member?.ID) setCouponAlertDismissedFor(member.ID);
        }}>稍後</button>
      </Modal>

      {/* 優惠券核銷確認視窗 */}
      <Modal 
        open={couponConfirm.open} 
        onClose={()=>setCouponConfirm({ open:false, coupon:null, busy:false, doneMsg:'' })} 
        width={520}
      >
        {!couponConfirm.doneMsg ? (
          <>
            <h3 style={{margin:'6px 0 14px',fontSize:22,fontWeight:900}}>確認兌換優惠券</h3>
            {(member?.NAME || couponConfirm.coupon?.memberName) && (
              <div style={{marginBottom:12,fontSize:18}}>
                會員：<b style={{fontSize:20}}>{member?.NAME || couponConfirm.coupon?.memberName}</b>
              </div>
            )}
            <div style={{marginBottom:12,fontSize:18}}>
              優惠券：<b style={{fontSize:20}}>{couponConfirm.coupon?.NAME || '—'}</b>
            </div>
            <div style={{marginBottom:16,fontSize:28,fontWeight:900,letterSpacing:1}}>
              兌換編號 {resolveDisplayCode(couponConfirm.coupon?.CLAIM_ID, couponConfirm.coupon?.DISPLAY_CODE)}
            </div>
            {couponConfirm.coupon?.END_AT && (
              <div style={{marginBottom:12,fontSize:16,color:'#555'}}>到期 {couponConfirm.coupon.END_AT}</div>
            )}
            {(couponConfirm.coupon?.phoneTail || couponConfirm.coupon?.emailMask) && (
              <div style={{marginBottom:12,fontSize:14,color:'#555'}}>
                {couponConfirm.coupon?.phoneTail ? `手機末四碼 ${couponConfirm.coupon.phoneTail}` : ''}
                {couponConfirm.coupon?.phoneTail && couponConfirm.coupon?.emailMask ? ' · ' : ''}
                {couponConfirm.coupon?.emailMask ? `Email ${couponConfirm.coupon.emailMask}` : ''}
              </div>
            )}
            {couponConfirm.coupon?.DESCRIPTION && (
              <div style={{marginBottom:12,padding:'10px 12px',background:'#f9fafb',border:'1px solid #e5e7eb',borderRadius:8}}>
                <div style={{fontSize:16,fontWeight:800,marginBottom:6,color:'#111'}}>備註：</div>
                <div style={{fontSize:16,color:'#374151',lineHeight:1.6,whiteSpace:'pre-wrap'}}>{couponConfirm.coupon.DESCRIPTION}</div>
              </div>
            )}
            <div style={{margin:'10px 0 16px',fontSize:15,color:'#555'}}>已提醒工作人員優惠內容</div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button 
                onClick={()=>setCouponConfirm({ open:false, coupon:null, busy:false, doneMsg:'' })} 
                disabled={couponConfirm.busy} 
                style={{padding:'10px 16px',fontSize:16}}
              >
                取消
              </button>
              <button 
                onClick={confirmRedeemCoupon} 
                disabled={couponConfirm.busy} 
                style={{padding:'10px 16px',fontSize:16,background:'#111',color:'#fff',borderRadius:8,fontWeight:800}}
              >
                {couponConfirm.busy ? '處理中…' : '確認兌換'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{margin:'6px 0 14px',fontSize:22,fontWeight:900}}>已完成</h3>
            <div style={{color:'#0a7',fontWeight:800,marginBottom:8,fontSize:18}}>{couponConfirm.doneMsg}</div>
            <div style={{marginBottom:16,fontSize:15,color:'#555'}}>已提醒工作人員優惠內容</div>
            <div style={{display:'flex',justifyContent:'flex-end'}}>
              <button 
                onClick={()=>setCouponConfirm({ open:false, coupon:null, busy:false, doneMsg:'' })} 
                style={{padding:'10px 16px',fontSize:16}}
              >
                關閉
              </button>
            </div>
          </>
        )}
      </Modal>

      <CouponUsedNoticeModal
        open={couponUsedNotice.open}
        onClose={() => setCouponUsedNotice({ open:false, couponName:'', claimId:'', displayCode:'', usedAt:'' })}
        couponName={couponUsedNotice.couponName}
        displayCode={couponUsedNotice.displayCode}
        usedAt={couponUsedNotice.usedAt}
        width={460}
      />
    </section>
  );
}
